import { AxiosResponse } from 'axios';
import cacheableRequest from '../../../services/cacheableRequest';
import { PathwayResponse } from '../models/pathwayResponse';
import { Entity } from '../models/entity';

type Transition = {
  from: string;
  to: string;
};

const orderPathwayEntities = (entities: Entity[], transitions: Transition[]): Entity[] => {
  // Find start entity
  const startEntity = entities.find(entity => entity._type === 'Start');
  if (!startEntity) return entities;

  const visited = new Set<string>();
  const orderedEntities: Entity[] = [];

  const traversePath = (currentId: string) => {
    if (visited.has(currentId)) return;

    const currentEntity = entities.find(e => e.id === currentId);
    if (!currentEntity) return;

    visited.add(currentId);
    orderedEntities.push(currentEntity);

    const nextTransition = transitions.find(t => t.from === currentId);
    if (nextTransition) {
      traversePath(nextTransition.to);
    }
  };

  // Start traversal from the start entity
  traversePath(startEntity.id);

  // Add end entity if not already included
  const endEntity = entities.find(entity => entity._type === 'End');
  if (endEntity && !visited.has(endEntity.id)) {
    orderedEntities.push(endEntity);
    visited.add(endEntity.id);
  }

  // Add any remaining entities that weren't connected in the transitions
  entities.forEach(entity => {
    if (!visited.has(entity.id)) {
      orderedEntities.push(entity);
    }
  });

  return orderedEntities;
};

export const getPathway = (workflowId: string): Promise<PathwayResponse> => {
  return cacheableRequest(`/v1/workflows/${workflowId}/detail`).then(({ data }: AxiosResponse<PathwayResponse>) => {
    const workflow = data;
    workflow.entities = orderPathwayEntities(workflow.entities, workflow.transitions);
    return workflow;
  });
};
